import FormDropdown, {
    FormDropdownOption,
    buildOptionsWithSections,
} from "./FormDropdown";
import { Controller, useFormContext } from "react-hook-form";
import { EmissionsSourceItems } from "../api/models/EmissionsSource";

export type ItemTypeValues = {
    categoryId: number;
    typeId: number;
    itemId: number;
};

type EmissionsSourceItemDetailFormProps = {
    emissionsSourceItems: EmissionsSourceItems | null;
    isLoadingEmissionsSourceItems: boolean;
};

const EmissionsSourceItemDetailForm = ({
    emissionsSourceItems,
    isLoadingEmissionsSourceItems,
}: EmissionsSourceItemDetailFormProps) => {
    const methods = useFormContext<ItemTypeValues>();

    const selectedCategoryId = methods.watch("categoryId");
    const selectedTypeId = methods.watch("typeId");

    const categoryOptions: FormDropdownOption<number>[] = emissionsSourceItems
        ? emissionsSourceItems.emissionsSourceCategories.map((_) => ({
              value: _.id,
              label: _.name,
          }))
        : [];

    const typeOptions: FormDropdownOption<number>[] = emissionsSourceItems
        ? emissionsSourceItems.emissionsSourceTypes
              .filter((_) => _.categoryId === selectedCategoryId)
              .map((_) => ({
                  value: _.id,
                  label: _.name,
              }))
        : [];

    const itemOptions: FormDropdownOption<number>[] = emissionsSourceItems
        ? emissionsSourceItems.emissionsSourceItems
              .filter((_) => _.typeId === selectedTypeId)
              .map((_) => ({
                  value: _.id,
                  label: _.name,
              }))
        : [];

    const categoryOptionsInSections = emissionsSourceItems
        ? buildOptionsWithSections(categoryOptions, (option) => {
              const emissionsSourceItem =
                  emissionsSourceItems.emissionsSourceCategories.find(
                      (_) => _.id === option.value
                  );

              return {
                  sectionTitle: `Scope ${emissionsSourceItem?.scope ?? 0} ${
                      emissionsSourceItem?.scope === 1
                          ? ": Direct"
                          : emissionsSourceItem?.scope === 2
                          ? " : Indirect"
                          : " : Supply chain"
                  }`,
                  sectionOrder: emissionsSourceItem?.scope ?? 0,
              };
          })
        : {};

    const handleTypeChange = (typeId: number) => {
        const itemsForSelectedType =
            emissionsSourceItems?.emissionsSourceItems.filter(
                (_) => _.typeId === typeId
            ) ?? [];

        if (itemsForSelectedType.length === 1) {
            const itemId = itemsForSelectedType[0].id;
            methods.setValue("itemId", itemId);
        } else {
            methods.setValue("itemId", -1);
        }
    };

    const handleCategoryChange = (categoryId: number) => {
        const typesForSelectedCategory =
            emissionsSourceItems?.emissionsSourceTypes.filter(
                (_) => _.categoryId === categoryId
            ) ?? [];
        if (typesForSelectedCategory.length === 1) {
            const typeId = typesForSelectedCategory[0].id;
            methods.setValue("typeId", typeId);
            handleTypeChange(typeId);
        } else {
            methods.setValue("typeId", -1);
            methods.setValue("itemId", -1);
        }
    };

    return (
        <>
            <Controller
                name="categoryId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        required
                        isLoadingOptions={isLoadingEmissionsSourceItems}
                        label="Category"
                        pleaseSelectText="Select category"
                        options={categoryOptionsInSections}
                        value={value}
                        onChange={(categoryId, option) => {
                            handleCategoryChange(categoryId);
                            onChange(categoryId, option);
                        }}
                        error={methods.formState.errors.categoryId}
                        buildSelectedTextOverride={(categoryId) => {
                            const selectedCategory =
                                emissionsSourceItems?.emissionsSourceCategories.find(
                                    (_) => _.id === categoryId
                                );

                            if (!selectedCategory) return undefined;

                            return `${selectedCategory.name} : Scope ${selectedCategory.scope}`;
                        }}
                    />
                )}
            />
            {(typeOptions.length > 1 || itemOptions.length > 1) && (
                <Controller
                    name="typeId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            disabled={selectedCategoryId === -1}
                            isLoadingOptions={isLoadingEmissionsSourceItems}
                            label="Type"
                            pleaseSelectText="Select type"
                            options={typeOptions}
                            value={value}
                            onChange={(typeId, option) => {
                                handleTypeChange(typeId);
                                onChange(typeId, option);
                            }}
                            error={
                                selectedCategoryId === -1
                                    ? undefined
                                    : methods.formState.errors.typeId
                            }
                        />
                    )}
                />
            )}
            {itemOptions.length > 1 && (
                <Controller
                    name="itemId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            disabled={selectedTypeId === -1}
                            isLoadingOptions={isLoadingEmissionsSourceItems}
                            label="Item"
                            pleaseSelectText="Select item"
                            options={itemOptions}
                            value={value}
                            onChange={onChange}
                            error={
                                selectedTypeId === -1
                                    ? undefined
                                    : methods.formState.errors.itemId
                            }
                        />
                    )}
                />
            )}
        </>
    );
};

export default EmissionsSourceItemDetailForm;
